import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Button } from './Button'
import { trackEvent } from '../lib/analytics'

interface AdvertorialProps {
  onContinue: () => void
}

/**
 * Pre-funnel advertorial page for the 'meta-advertorial' variant.
 *
 * Linda story (anonymized composite, not a single named patient). Designed to
 * warm cold paid traffic before entering the full meta-compliant funnel.
 *
 * Compliance notes:
 *   - No outcome claims (no "improved labs", "slowed progression", "avoided dialysis")
 *   - No medical damage claims
 *   - No life-saving language
 *   - Frames KidneyPal as a tool that helps with a renal diet, not as treatment
 *   - "Linda" is described as a representative composite, which is disclosed
 */
export function Advertorial({ onContinue }: AdvertorialProps) {
  useEffect(() => {
    trackEvent('advertorial_view')
  }, [])

  const handleContinue = () => {
    trackEvent('advertorial_cta_click')
    onContinue()
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar — branded, looks like an article header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/images/icon.png" alt="" className="w-7 h-7 rounded-lg" />
            <span className="font-bold text-gray-900">KidneyPal</span>
          </div>
          <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Story</span>
        </div>
      </header>

      <article className="max-w-2xl mx-auto px-5 py-8 md:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          {/* Eyebrow + headline */}
          <p className="text-xs uppercase tracking-widest text-primary font-bold mb-3">
            A Reader's Story
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
            "I Was Terrified Every Time I Went Grocery Shopping"
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            How one woman went from staring blankly at food labels to walking out of
            the store in under twenty minutes — without second-guessing a single item.
          </p>

          {/* Author / dateline */}
          <div className="flex items-center gap-3 pb-6 mb-6 border-b border-gray-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
              KP
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">KidneyPal Editorial</p>
              <p className="text-xs text-gray-500">5 min read</p>
            </div>
          </div>

          {/* Hero image / illustration */}
          <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-teal-50 to-amber-50 aspect-[16/10] mb-8 flex items-center justify-center">
            <img
              src="/images/mascot-clipboard.png"
              alt=""
              className="w-32 h-32 md:w-40 md:h-40 object-contain"
            />
          </div>

          {/* Body */}
          <div className="prose prose-gray max-w-none text-gray-800 leading-relaxed space-y-5">
            <p>
              The day Linda was diagnosed with Stage 3 chronic kidney disease, her
              nephrologist handed her a single-page printout. Sodium. Potassium.
              Phosphorus. A short list of foods to avoid, a shorter list of foods
              to favor, and a polite "any questions?"
            </p>

            <p>
              She had questions. She had hundreds. But the appointment was already
              over, and the next one wasn't for ninety days.
            </p>

            <p className="text-xl font-semibold text-gray-900 leading-snug border-l-4 border-primary pl-5 my-6">
              "I stood in the cereal aisle for forty minutes the first time," Linda
              told us. "I was reading every box like a textbook. By the end I was
              almost in tears."
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">
              The information was everywhere — and nowhere
            </h2>

            <p>
              Linda did what most newly diagnosed kidney patients do. She googled.
              She joined Facebook groups. She bought two books. She watched videos.
              And the more she read, the more contradicted she felt.
            </p>

            <p>
              One source said tomatoes were a problem. Another said only if you
              were on dialysis. A third listed them as kidney-friendly. The label
              on a frozen meal said "low sodium" but the potassium chloride further
              down the ingredients list told a different story.
            </p>

            <p>
              "I started taking pictures of every label and texting them to my
              dietitian," she said. "She finally told me — kindly — that she
              couldn't be on call for grocery runs."
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">
              What actually changed things
            </h2>

            <p>
              Linda found KidneyPal through a kidney foundation newsletter. She
              downloaded it expecting "another tracker." What she got was a tool
              that let her point her phone at a meal — restaurant plate, home-cooked
              dinner, takeout container, whatever was in front of her — and see the
              nutrients that mattered for her stage of CKD.
            </p>

            <p>
              The app didn't tell her what she <em>couldn't</em> eat. It showed her
              what was actually <em>in</em> the food and how it lined up with the
              daily targets her care team had given her.
            </p>

            <p>
              "It's the first thing I've used that doesn't treat me like I'm five,"
              she said. "I'm a grown woman with a medical condition. I don't need
              the app to be the doctor. I need it to do the math while I'm holding
              two grocery bags."
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">
              The unglamorous routine that's working
            </h2>

            <p>
              Linda's routine now is, in her own words, "boring on purpose."
            </p>

            <ul className="list-disc pl-6 space-y-2 my-4">
              <li>
                She scans her breakfast every morning. Takes about eight seconds.
              </li>
              <li>
                At the store, she scans the barcode of anything new. If the
                phosphorus, sodium, or potassium is over her budget, she puts it
                back. If it's not, it goes in the cart.
              </li>
              <li>
                Once a week, she exports a one-page summary to send to her
                dietitian before their check-in.
              </li>
            </ul>

            <p>
              "It's not magic," she said. "It's a calculator with a camera and
              a memory. But for me, that's what was missing."
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-3">
              The takeaway, for anyone newly diagnosed
            </h2>

            <p>
              Linda's biggest piece of advice for the newly diagnosed isn't about
              an ingredient or a recipe. It's about the gap between leaving the
              nephrologist's office and your next appointment.
            </p>

            <p>
              "Nobody is going to be standing next to you in the grocery store,"
              she said. "You have to figure out a system that works for you in
              that moment. For me, that ended up being an app on my phone. For
              somebody else it might be something else. But you need <em>something</em>
              between visits."
            </p>

            <p className="text-sm text-gray-500 italic mt-8 pt-6 border-t border-gray-100">
              Linda is a composite based on conversations with KidneyPal members
              who shared their stories with us. Names and identifying details
              have been changed. KidneyPal is an educational and tracking tool
              and is not a substitute for medical advice from your nephrologist
              or dietitian.
            </p>
          </div>

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-12 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 md:p-8 text-center"
          >
            <img
              src="/images/mascot-holdingveggies.png"
              alt=""
              className="w-20 h-20 mx-auto mb-3 object-contain"
            />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              See what your renal plan would look like
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Answer a few questions about your kidney health and we'll show you
              the nutrient targets and tools KidneyPal would build around your
              profile.
            </p>
            <Button onClick={handleContinue}>
              Build my kidney plan
            </Button>
            <p className="text-xs text-gray-500 mt-4">
              Free to start · Takes about 2 minutes
            </p>
          </motion.div>
        </motion.div>
      </article>

      <footer className="py-6 px-5 text-center text-xs text-gray-400 border-t border-gray-100">
        <p className="mb-2 max-w-md mx-auto">
          KidneyPal is for informational purposes and is not a substitute for
          medical advice. Consult your nephrologist or dietitian before making
          dietary changes.
        </p>
        <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
          Terms of Service
        </a>
        <span className="mx-2">·</span>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
          Privacy Policy
        </a>
        <span className="mx-2">·</span>
        <a href="mailto:support@kidneypal.app" className="hover:text-gray-600">
          Contact
        </a>
      </footer>
    </div>
  )
}
